import { VesselModel } from "../model/types";
import {
  SeaContainerDTO,
  SeaContainerEventDTO,
  SeaContainerViewDTO,
  SeaEventCodesDTO,
  SeaEventDTO,
  SeaLocationDTO,
  SeaLocationFlatDTO,
  SeaLocationTypesDTO,
  SeaMetadataDTO,
  SeaResponseDTO,
  SeaRouteDetailsDTO,
  SeaRouteDTO,
  SeaRouteNodeDTO,
  SeaTransportModeDTO,
} from "./response.types";
import {
  SeaMessagesDTO,
  SeaReferenceTypesDTO,
  SeaRouteTypesDTO,
  SeaTransportTypesDTO,
  ShipmentStatusesDTO,
} from "entities/shared/seaCard.types";
import { CoordinateModelDTO, DateTimeDTO, SearchReferenceDTO } from "entities/shared/shipmentCard.types";
import { isNumber, Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { generateContainersSizeTypes } from "./utils";

export type TransformedDemurageDTO = {
  number: SeaContainerViewDTO["number"];
  charges: NonNullable<SeaContainerViewDTO["charges"]>;
}[];

export type SeaTransformedEventDTO = { location: string | null; events: SeaEventDTO[] };

const SUCCESS_STATUS_CODES: ValueOf<typeof SeaMessagesDTO>[] = [
  SeaMessagesDTO.OK,
  SeaMessagesDTO.NO_CONTAINERS,
  SeaMessagesDTO.NO_EVENTS,
];

const API_KEY_LIMIT_CODES: ValueOf<typeof SeaMessagesDTO>[] = [
  SeaMessagesDTO.API_KEY_QUOTA_EXCEEDED,
  SeaMessagesDTO.API_KEY_RATE_LIMIT_EXCEEDED,
  SeaMessagesDTO.API_KEY_ACCESS_DENIED,
  SeaMessagesDTO.API_KEY_EXPIRED,
  SeaMessagesDTO.INVALID_API_KEY,
];

const POL_EVENT_CODES = [SeaEventCodesDTO.LOAD, SeaEventCodesDTO.DEPA, SeaEventCodesDTO.GTIN];
const POD_EVENT_CODES = [SeaEventCodesDTO.ARRI, SeaEventCodesDTO.DISC, SeaEventCodesDTO.GTOT];

const mapReferenceTypeFromApi = (type?: string | null): ValueOf<typeof SeaReferenceTypesDTO> | null => {
  if (!type) return null;
  const normalized = type.trim().toUpperCase();
  if (normalized === "BL" || normalized === "BILL_OF_LADING") return SeaReferenceTypesDTO.BL;
  if (normalized === "BK" || normalized === "BOOKING") return SeaReferenceTypesDTO.BK;
  if (normalized === "CT" || normalized === "CONTAINER") return SeaReferenceTypesDTO.CT;
  return null;
};

const mapTransportToRouteType = (mode?: SeaTransportModeDTO | null): ValueOf<typeof SeaRouteTypesDTO> => {
  switch (mode) {
    case SeaTransportTypesDTO.TRUCK:
    case SeaTransportTypesDTO.RAIL:
      return SeaRouteTypesDTO.LAND;
    case "AIR":
      return SeaRouteTypesDTO.AIR;
    case SeaTransportTypesDTO.VESSEL:
    case SeaTransportTypesDTO.FEEDER:
    case SeaTransportTypesDTO.BARGE:
    default:
      return SeaRouteTypesDTO.SEA;
  }
};

const mapTransportType = (mode?: SeaTransportModeDTO | null): ValueOf<typeof SeaTransportTypesDTO> | null => {
  if (!mode) return null;
  if (mode === "AIR") return SeaTransportTypesDTO.AIR;
  if ((Object.values(SeaTransportTypesDTO) as string[]).includes(mode)) {
    return mode as ValueOf<typeof SeaTransportTypesDTO>;
  }
  return null;
};

const normalizeEvent = (event: SeaContainerEventDTO): SeaEventDTO => ({
  location: event.location_id,
  description: event.description,
  status: event.event_code,
  date: event.datetime,
  actual: event.is_actual,
  vessel: event.transport_id,
  voyage: event.voyage_number,
  type: mapTransportToRouteType(event.transport_mode),
  transportType: mapTransportType(event.transport_mode),
  location_type: event.location_type,
});

const normalizeContainer = (container: SeaContainerDTO): SeaContainerViewDTO => ({
  number: container.number,
  size_type: container.type ? `${container.size}' ${container.type}` : String(container.size),
  iso_code: container.iso_code,
  events: (container.events ?? []).map(normalizeEvent),
  status: container.status,
  charges: null,
});

const flattenLocation = (location: SeaLocationDTO): SeaLocationFlatDTO => ({
  id: location.id,
  name: location.name,
  country: location.country?.name ?? null,
  country_code: location.country?.code ?? null,
  locode: location.unlocode,
  lat: location.coordinates?.lat ?? null,
  lng: location.coordinates?.lng ?? null,
});

export class SearchApiParser {
  private model: SeaResponseDTO;

  private _etd: string | null = null;
  private _atd: string | null = null;
  private _eta: string | null = null;
  private _ata: string | null = null;

  private _pol: string | null = null;
  private _pod: string | null = null;

  private vessels: VesselModel[] = [];
  private flatLocations: SeaLocationFlatDTO[] = [];
  private viewContainers: SeaContainerViewDTO[] = [];

  public readonly isSuccess: boolean = false;
  public readonly hasData: boolean = false;
  public readonly requestLimitReached: boolean = false;

  constructor(data: SeaResponseDTO) {
    this.model = data;

    this.flatLocations = (this.model.data?.locations ?? []).map(flattenLocation);
    this.viewContainers = (this.model.data?.containers ?? []).map(normalizeContainer);

    this.setDates();
    this.setLocations();
    this.setVessels();

    this.isSuccess = Boolean(this.model.success) && SUCCESS_STATUS_CODES.includes(this.message);

    // Guest limit from the back office, or API-key quota from the gateway.
    this.requestLimitReached =
      this.model.requestLimitReachedWebApp === true || API_KEY_LIMIT_CODES.includes(this.message);

    this.hasData = this.containersAmount > 0 && Boolean(this.model.success) && this.message === SeaMessagesDTO.OK;
  }

  get message(): ValueOf<typeof SeaMessagesDTO> {
    return this.model.status_code;
  }

  get reference(): SearchReferenceDTO | null {
    return this.model.data?.shipment?.reference_number ?? this.model.meta?.request?.params?.reference_number ?? null;
  }

  get referenceType(): ValueOf<typeof SeaReferenceTypesDTO> | null {
    return mapReferenceTypeFromApi(
      this.model.data?.shipment?.reference_type ?? this.model.meta?.request?.params?.reference_type
    );
  }

  get containersAmount(): number {
    return this.viewContainers.length;
  }

  get status(): ValueOf<typeof ShipmentStatusesDTO> | null {
    return this.model.data?.shipment?.status ?? null;
  }

  get carrierName(): string | null {
    return this.model.data?.shipment?.carrier?.name ?? null;
  }

  public getCarrierScac({ withDefault = false }: { withDefault?: boolean } = {}): string | null {
    return this.model.data?.shipment?.carrier?.code ?? (withDefault ? "AUTO" : null);
  }

  get fromDirection(): string | null {
    return this._pol;
  }

  get toDirection(): string | null {
    return this._pod;
  }

  public getLocationName(location: Nullable<SeaLocationFlatDTO>): string | null {
    if (!location) return null;
    if (location.country_code && location.name) {
      return `${location.name}, ${location.country_code}`;
    }
    return location.name ?? location.country ?? location.country_code;
  }

  public getLocationById(locationId: Nullable<number>): SeaLocationFlatDTO | null {
    return this.flatLocations.find((item) => item.id === locationId) ?? null;
  }

  get polCoordinate(): CoordinateModelDTO | null {
    // main uses place_of_receipt for the origin pin
    const locationId =
      this.model.data?.route?.place_of_receipt?.location_id ??
      this.model.data?.route?.first_port_of_loading?.location_id ??
      null;
    const location = this.getLocationById(locationId);
    if (!location || !isNumber(location.lat) || !isNumber(location.lng)) return null;
    return { lat: location.lat, lng: location.lng };
  }

  get podCoordinate(): CoordinateModelDTO | null {
    const locationId = this.model.data?.route?.final_port_of_discharge?.location_id ?? null;
    const location = this.getLocationById(locationId);
    if (!location || !isNumber(location.lat) || !isNumber(location.lng)) return null;
    return { lat: location.lat, lng: location.lng };
  }

  /** Same source as main: containers[0].route_details (fallback: data.route_details). */
  private getRouteDetails(): SeaRouteDetailsDTO | null {
    return this.model.data?.containers?.[0]?.route_details ?? this.model.data?.route_details ?? null;
  }

  get routeData(): SeaRouteDetailsDTO | null {
    const details = this.getRouteDetails();
    const legs = details?.route_legs;

    if (legs?.length) {
      const hasDrawablePath = legs.some((leg) => (leg.route_path?.length ?? 0) > 0);
      if (hasDrawablePath) {
        return {
          ...details,
          route_legs: [...legs].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
        };
      }
    }

    // Fallback: straight origin → destination when legs/paths are missing.
    const pol = this.polCoordinate;
    const pod = this.podCoordinate;
    if (pol && pod) {
      return {
        ...(details ?? {}),
        route_legs: [
          {
            sequence: 1,
            route_path: [
              [pol.lat, pol.lng],
              [pod.lat, pod.lng],
            ],
            transport_mode: SeaTransportTypesDTO.VESSEL,
          },
        ],
      };
    }

    return details?.route_legs?.length ? details : null;
  }

  get currentPin(): CoordinateModelDTO | null {
    const details = this.getRouteDetails();
    const predicted = details?.predicted_position?.coordinates;
    if (predicted && isNumber(predicted.lat) && isNumber(predicted.lng)) {
      return { lat: predicted.lat, lng: predicted.lng };
    }

    const ais = details?.latest_ais_data?.coordinates;
    if (ais && isNumber(ais.lat) && isNumber(ais.lng)) {
      return { lat: ais.lat, lng: ais.lng };
    }

    return null;
  }

  etd(): DateTimeDTO | null {
    return this._etd;
  }

  atd(): DateTimeDTO | null {
    return this._atd;
  }

  eta(): DateTimeDTO | null {
    return this._eta;
  }

  ata(): DateTimeDTO | null {
    return this._ata;
  }

  get predictiveEta(): DateTimeDTO | null {
    return this.model.data?.route?.final_port_of_discharge?.arrival?.datetime ?? null;
  }

  get containers(): SeaContainerViewDTO[] {
    return this.viewContainers;
  }

  get demurage(): TransformedDemurageDTO {
    const demurageData: TransformedDemurageDTO = [];

    this.viewContainers.forEach((container) => {
      const charges = container.charges;
      if (!charges) return;

      const values = [
        charges.storage?.free_days,
        charges.storage?.days_in_charge,
        charges.demurrage?.free_days,
        charges.demurrage?.days_in_charge,
        charges.detention?.free_days,
        charges.detention?.days_in_charge,
      ];

      if (values.some((charge) => !!charge)) {
        demurageData.push({
          number: container.number,
          charges,
        });
      }
    });

    return demurageData;
  }

  get containersSizeTypes(): string[] {
    const rawSizeTypes = this.viewContainers.map((container) => container.size_type).filter(Boolean) as string[];
    return generateContainersSizeTypes(rawSizeTypes);
  }

  get events(): SeaEventDTO[] {
    return this.viewContainers[0]?.events ?? [];
  }

  public getTransformedEvents(targetEvents: Nullable<SeaEventDTO[]>): SeaTransformedEventDTO[] {
    if (!targetEvents) return [];

    const events: SeaEventDTO[] = [];
    const transformedEvents: SeaTransformedEventDTO[] = [{ location: null, events }];
    let prevLocation: number | null = null;

    targetEvents.forEach((event, i) => {
      const location = this.getLocationById(event.location);
      const locationName = this.getLocationName(location);

      if (i === 0) {
        transformedEvents[0].location = locationName;
        transformedEvents[0].events.push(event);
      } else if (prevLocation === event.location) {
        transformedEvents[transformedEvents.length - 1].events.push(event);
      } else {
        transformedEvents.push({ location: locationName, events: [event] });
      }

      prevLocation = event.location;
    });

    return transformedEvents;
  }

  public getVessels(): VesselModel[] {
    return this.vessels;
  }

  get searchError(): ValueOf<typeof SeaMessagesDTO> {
    return this.message;
  }

  get locations(): SeaLocationFlatDTO[] {
    return this.flatLocations;
  }

  get metadata(): SeaMetadataDTO | undefined {
    const shipment = this.model.data?.shipment;
    if (!shipment) return undefined;

    return {
      type: this.referenceType,
      number: shipment.reference_number,
      requested_number: this.reference ?? shipment.reference_number,
      sealine: shipment.carrier.code,
      sealine_name: shipment.carrier.name,
      status: shipment.status,
      updated_at: shipment.updated_at,
    };
  }

  private getNodeDate(
    node: SeaRouteNodeDTO | null | undefined,
    kind: "arrival" | "departure"
  ): { date: string | null; actual: boolean | null } {
    const point = node?.[kind];
    return {
      date: point?.datetime ?? null,
      actual: point?.is_actual ?? null,
    };
  }

  private setLocations() {
    // Align with main: POL label from place_of_receipt, POD from final discharge.
    const polId =
      this.model.data?.route?.place_of_receipt?.location_id ??
      this.model.data?.route?.first_port_of_loading?.location_id ??
      this.findLocationIdByType(SeaLocationTypesDTO.FIRST_PORT_OF_LOADING);
    const podId =
      this.model.data?.route?.final_port_of_discharge?.location_id ??
      this.findLocationIdByType(SeaLocationTypesDTO.FINAL_PORT_OF_DISCHARGE);

    this._pol = this.getLocationName(this.getLocationById(polId));
    this._pod = this.getLocationName(this.getLocationById(podId));
  }

  private findLocationIdByType(locationType: ValueOf<typeof SeaLocationTypesDTO>): number | null {
    for (const container of this.viewContainers) {
      const event = container.events.find((item) => item.location_type === locationType);
      if (event?.location != null) return event.location;
    }
    return null;
  }

  private setDates() {
    const pol = this.getNodeDate(this.model.data?.route?.first_port_of_loading, "departure");
    if (pol.date !== null) {
      if (pol.actual === true) this._atd = pol.date;
      else if (pol.actual === false) this._etd = pol.date;
      else this._etd = pol.date;
    }

    const pod = this.getNodeDate(this.model.data?.route?.final_port_of_discharge, "arrival");
    if (pod.date !== null) {
      if (pod.actual === true) this._ata = pod.date;
      else if (pod.actual === false) this._eta = pod.date;
      else this._eta = pod.date;
    }
  }

  private setVessels(): void {
    const transports = this.model.data?.transports ?? [];
    const vessels: VesselModel[] = [];

    for (const transport of transports) {
      vessels.push({
        name: transport.name ?? "",
        voyage: this.getVoyageByVesselId(transport.id),
        pol: this.getLocationByVesselIdAndEventCodes(transport.id, POL_EVENT_CODES),
        pod: this.getLocationByVesselIdAndEventCodes(transport.id, POD_EVENT_CODES),
        etd: this.getDateByVesselIdAndEventCodes(transport.id, POL_EVENT_CODES, false, true),
        atd: this.getDateByVesselIdAndEventCodes(transport.id, POL_EVENT_CODES, true, true),
        eta: this.getDateByVesselIdAndEventCodes(transport.id, POD_EVENT_CODES, false, false),
        ata: this.getDateByVesselIdAndEventCodes(transport.id, POD_EVENT_CODES, true, false),
        predictiveETA: this.predictiveEta,
      });
    }

    this.vessels = vessels;
  }

  private getLocationByVesselIdAndEventCodes(vesselId: number, eventCodes: string[]): string | null {
    const event =
      this.viewContainers[0]?.events.find(
        (item) => item.vessel === vesselId && eventCodes.includes(String(item.status))
      ) ?? null;
    return this.getLocationName(this.getLocationById(event?.location ?? null));
  }

  private getVoyageByVesselId(vesselId: number): string | null {
    return this.viewContainers[0]?.events.find((item) => item.vessel === vesselId)?.voyage ?? null;
  }

  private getDateByVesselIdAndEventCodes(
    vesselId: number,
    eventCodes: string[],
    isActual: boolean,
    isReverse: boolean
  ): string | null {
    const events = this.viewContainers[0]?.events ?? [];
    const list = isReverse ? [...events].reverse() : events;
    const event =
      list.find(
        (item) => item.vessel === vesselId && eventCodes.includes(String(item.status)) && item.actual === isActual
      ) ?? null;
    return event?.date ?? null;
  }
}
